import { useState } from "react";
import { createTrade, getTradesByUser, useAction, useQuery } from "wasp/client/operations";
import { useAuth } from "wasp/client/auth";
import { PaymentPlanId } from "../payment/plans";
import { Button } from "../client/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../client/components/ui/dialog";
import { toast } from "../client/hooks/use-toast";
import useTranslation from "../client/hooks/useTranslation";
import TradeForm, { type TradeFormData, initialTradeFormData } from "./TradeForm";
import { useAccountBlewUpModal } from "../trading-accounts/components/useAccountBlewUpModal";
import { AccountBlewUpModal } from "../trading-accounts/components/AccountBlewUpModal";
import { useAutoDetectSessionToast } from "./useAutoDetectSessionToast";

/** Seuil d'avertissement du plan Free (inchangé depuis TradingJournalPage). */
const FREE_PLAN_TRADE_WARNING_THRESHOLD = 40;

interface ManualTradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreated?: () => void;
  defaultAccountId?: string | null;
  /**
   * Nombre de trades servant à l'avertissement du plan Free. Le journal passe
   * son propre total (filtré) pour conserver son comportement historique ;
   * absent, le composant lit lui-même le total non filtré.
   */
  tradeCountForLimit?: number;
}

/**
 * Modale de création manuelle d'un trade (formulaire + avertissement de
 * limite du plan Free), extraite de TradingJournalPage pour être réutilisable.
 */
export default function ManualTradeDialog({
  open,
  onOpenChange,
  onCreated,
  defaultAccountId,
  tradeCountForLimit,
}: ManualTradeDialogProps) {
  const t = useTranslation();
  const { data: user } = useAuth();
  const isFreePlan = !user?.subscriptionPlan || user.subscriptionPlan === PaymentPlanId.Free;

  const createTradeFn = useAction(createTrade);
  const { handleViolation, blewUpModalProps } = useAccountBlewUpModal();
  const handleAutoDetectResult = useAutoDetectSessionToast();

  const { data: ownCountData } = useQuery(
    getTradesByUser,
    { skip: 0, take: 1 },
    { enabled: isFreePlan && tradeCountForLimit === undefined },
  );
  const tradeCount = tradeCountForLimit ?? (ownCountData as any)?.total;

  const buildInitialForm = (): TradeFormData => ({
    ...initialTradeFormData,
    accountId: defaultAccountId || initialTradeFormData.accountId,
  });

  const [formData, setFormData] = useState<TradeFormData>(buildInitialForm);
  const [isLimitWarningOpen, setIsLimitWarningOpen] = useState(false);

  // Le formulaire repart à neuf à chaque ouverture (comme le faisait la page
  // avant d'ouvrir la modale). Réinitialisé pendant le rendu pour éviter
  // d'afficher une frame avec l'ancienne saisie.
  const [prevOpen, setPrevOpen] = useState(open);
  if (open !== prevOpen) {
    setPrevOpen(open);
    if (open) setFormData(buildInitialForm());
  }

  const executeCreateTrade = async () => {
    try {
      const createResult = await createTradeFn({
        date: new Date(formData.date),
        symbol: formData.symbol,
        type: formData.type,
        status: formData.status,
        entryPrice: parseFloat(formData.entryPrice),
        exitPrice: formData.exitPrice ? parseFloat(formData.exitPrice) : null,
        quantity: parseFloat(formData.quantity),
        pnl: formData.pnl && formData.status === "closed" ? parseFloat(formData.pnl) : null,
        commission: parseFloat(formData.commission) || 0,
        swaps: formData.swaps ? parseFloat(formData.swaps) : null,
        notes: formData.notes || null,
        setupRating: formData.setupRating,
        tagIds: formData.tagIds,
        accountId: formData.accountId || null,
        customFields: formData.customFields,
      });
      handleAutoDetectResult((createResult as any)?.autoDetectResult);
      handleViolation((createResult as any)?.accountViolation);
      toast({
        title: t.journal.tradeCreated,
        description: t.journal.tradeCreatedDescription,
      });
      onOpenChange(false);
      setIsLimitWarningOpen(false);
      setFormData(initialTradeFormData);
      onCreated?.();
    } catch (error: any) {
      toast({
        title: t.common.error,
        description: error.message || t.journal.cannotCreate,
        variant: "destructive",
      });
    }
  };

  const handleCreate = async () => {
    if (!formData.date) {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.date), variant: "destructive" });
      return;
    }
    if (!formData.symbol || formData.symbol.trim() === "") {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.symbol), variant: "destructive" });
      return;
    }
    if (!formData.entryPrice || isNaN(parseFloat(formData.entryPrice)) || parseFloat(formData.entryPrice) <= 0) {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.entryPrice), variant: "destructive" });
      return;
    }
    if (!formData.quantity || isNaN(parseFloat(formData.quantity)) || parseFloat(formData.quantity) <= 0) {
      toast({ title: t.common.error, description: t.validation.fieldRequired.replace("{field}", t.journal.quantity), variant: "destructive" });
      return;
    }

    if (isFreePlan && tradeCount >= FREE_PLAN_TRADE_WARNING_THRESHOLD) {
      setIsLimitWarningOpen(true);
      return;
    }

    await executeCreateTrade();
  };

  return (
    <>
      {/* Create Dialog */}
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t.journal.newTrade}</DialogTitle>
            <DialogDescription>{t.journal.subtitle}</DialogDescription>
          </DialogHeader>
          <TradeForm
            formData={formData}
            setFormData={setFormData}
            onSubmit={handleCreate}
            onCancel={() => {
              onOpenChange(false);
              setFormData(initialTradeFormData);
            }}
          />
        </DialogContent>
      </Dialog>

      {/* Trade Limit Warning Dialog */}
      <Dialog open={isLimitWarningOpen} onOpenChange={setIsLimitWarningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t.common.tradeLimitReached}</DialogTitle>
            <DialogDescription>{t.common.tradeLimitWarning}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsLimitWarningOpen(false)}>{t.common.cancel}</Button>
            <Button onClick={executeCreateTrade}>{t.common.proceed}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AccountBlewUpModal {...blewUpModalProps} />
    </>
  );
}
